/**
 * Pilhas de desfazer/refazer genéricas, sem saber nada sobre `CenaTabletop`.
 *
 * `passado` guarda os estados ANTERIORES a cada mudança confirmada, do mais
 * antigo ao mais recente; `futuro` guarda os estados que `desfazer` afastou,
 * para `refazer` devolvê-los. Registrar uma mudança nova sempre limpa
 * `futuro` — a metáfora de editor de texto padrão: desfazer, refazer e editar
 * de novo descarta o "futuro" anterior.
 */
export interface Historico<T> {
  readonly passado: readonly T[]
  readonly futuro: readonly T[]
}

export const LIMITE_HISTORICO = 50

export function historicoVazio<T>(): Historico<T> {
  return { passado: [], futuro: [] }
}

/** Registra `anterior` (o estado de ANTES da mudança) como um passo de desfazer. */
export function registrar<T>(historico: Historico<T>, anterior: T): Historico<T> {
  return {
    passado: [...historico.passado, anterior].slice(-LIMITE_HISTORICO),
    futuro: [],
  }
}

export interface ResultadoHistorico<T> {
  readonly historico: Historico<T>
  readonly estado: T
}

/** `atual` é o estado corrente (antes de desfazer), para poder virar um passo de refazer. */
export function desfazer<T>(historico: Historico<T>, atual: T): ResultadoHistorico<T> | null {
  if (historico.passado.length === 0) return null
  const estado = historico.passado[historico.passado.length - 1] as T
  return {
    estado,
    historico: {
      passado: historico.passado.slice(0, -1),
      futuro: [...historico.futuro, atual].slice(-LIMITE_HISTORICO),
    },
  }
}

/** `atual` é o estado corrente (antes de refazer), para poder voltar a virar um passo de desfazer. */
export function refazer<T>(historico: Historico<T>, atual: T): ResultadoHistorico<T> | null {
  if (historico.futuro.length === 0) return null
  const estado = historico.futuro[historico.futuro.length - 1] as T
  return {
    estado,
    historico: {
      passado: [...historico.passado, atual].slice(-LIMITE_HISTORICO),
      futuro: historico.futuro.slice(0, -1),
    },
  }
}

export const podeDesfazer = <T>(historico: Historico<T>): boolean => historico.passado.length > 0
export const podeRefazer = <T>(historico: Historico<T>): boolean => historico.futuro.length > 0
